import pricingModel from 'helpers/pricing.json';
import { Pricing } from './pricing_gen';
import { planPriority, Plans, showPlans } from './plans';
import { getPricingModelSessions } from '.';

const ALL_SYMBOL = '*';

// ClientSideFeatures
const ClientSideFeatures = {
  // billing & services
  Term: 'term',
  PaymentType: 'payment_type',
  EmailSupport: 'email_support',
  ChatSupport: 'chat_support',
  CustomPaperwork: 'custom_paperwork',
  Training: 'training',

  // privacy
  ContentExclude: 'content_exclude',
  GDPR: 'gdpr',
  CCPA: 'ccpa',

  // features usage
  SessionsLimit: 'sessions_limit',
  DataRetention: 'data_retention',

  // session replays
  SessionReplay: 'session_replay',
  PlaybackSpeed: 'playback_speed',
  Autoplay: 'autoplay',
  FormAnalytics: 'forms_analytics',
};

export const FeaturesBillingAndService = {
  Term: ClientSideFeatures.Term,
  PaymentType: ClientSideFeatures.PaymentType,
  EmailSupport: ClientSideFeatures.EmailSupport,
  ChatSupport: ClientSideFeatures.ChatSupport,
  CustomPaperwork: ClientSideFeatures.CustomPaperwork,
  Training: ClientSideFeatures.Training,
};

export const FeaturesPrivacy = {
  ContentExclude: ClientSideFeatures.ContentExclude,
  GDPR: ClientSideFeatures.GDPR,
  CCPA: ClientSideFeatures.CCPA,
};

export const FeaturesUsage = {
  SessionsLimit: ClientSideFeatures.SessionsLimit,
  DataRetention: ClientSideFeatures.DataRetention,
  Seats: Pricing.features.team_members,
  Websites: Pricing.features.websites,
};

export const FeaturesSessionReplays = {
  SessionReplay: ClientSideFeatures.SessionReplay,
  ClickMaps: Pricing.features.click_maps,
  Heatmaps: Pricing.features.heat_maps,
  EngagementScore: Pricing.features.engagement_score,
  PlaybackSpeed: ClientSideFeatures.PlaybackSpeed,
  SkipInactivity: Pricing.features.skip_inactivity,
  Autoplay: ClientSideFeatures.Autoplay,
  RecordingRules: Pricing.features.recording_rules,
  RageAndErrorClicks: Pricing.features.rage_and_error_clicks,
  Filters: Pricing.features.filters,
  FormAnalytics: ClientSideFeatures.FormAnalytics,
  SessionsSharing: Pricing.features.session_sharing,
  MinDuration: Pricing.features.min_duration,
  Tagging: Pricing.features.tagging,
  ResourceSynchronization: Pricing.features.resource_synchronization,
  DeveloperTools: Pricing.features.developer_tools,
};

export const FeaturesAnalytics = {
  Funnels: Pricing.features.funnels,
  Dashboard: Pricing.features.dashboards,
  UserJourney: Pricing.features.user_journey,
  Segments: Pricing.features.segments,
  SegmentNotifications: Pricing.features.segment_notifications,
  DataExport: Pricing.features.data_export,
};

export const FeaturesOther = {
  Integrations: Pricing.features.integrations,
  PremiumIntegrations: Pricing.features.premium_integrations,
  UserIdentification: Pricing.features.user_identification,
  CustomProperties: Pricing.features.custom_properties,
  MultiFactorAuthentication: Pricing.features.multi_factor_authentication,
  Notes: Pricing.features.notes,
  CustomEvents: Pricing.features.custom_events,
  RestAPI: Pricing.features.rest_api,
  AuditLogs: Pricing.features.audit_logs,
  SSO: Pricing.features.sso,
  Alerts: Pricing.features.alerts,
  Webhooks: Pricing.features.webhooks,
  CustomUserAccess: Pricing.features.custom_user_access,
  Organizations: Pricing.features.organizations,
};

export const Features = {
  BillingAndService: FeaturesBillingAndService,
  Privacy: FeaturesPrivacy,
  Usage: FeaturesUsage,
  SessionReplays: FeaturesSessionReplays,
  Analytics: FeaturesAnalytics,
  Other: FeaturesOther,
};

function clientSideFeatureConditions(plan) {
  const currentPlanPriority = planPriority(plan);
  const sessionsCountOptions = getPricingModelSessions(pricingModel);

  let features = {};

  if (currentPlanPriority >= planPriority(Plans.Free)) {
    features = {
      ...features,
      [ClientSideFeatures.EmailSupport]: true,

      [ClientSideFeatures.ContentExclude]: true,
      [ClientSideFeatures.GDPR]: true,
      [ClientSideFeatures.CCPA]: true,

      [ClientSideFeatures.SessionsLimit]: sessionsCountOptions.free[0].value,

      [ClientSideFeatures.DataRetention]: '1 month',

      [ClientSideFeatures.Term]: false,
      [ClientSideFeatures.PaymentType]: false,
      [ClientSideFeatures.ChatSupport]: false,
      [ClientSideFeatures.CustomPaperwork]: false,
      [ClientSideFeatures.Training]: false,

      [ClientSideFeatures.SessionReplay]: true,
      [ClientSideFeatures.PlaybackSpeed]: true,
      [ClientSideFeatures.Autoplay]: true,
      [ClientSideFeatures.FormAnalytics]: true,
    };
  }

  if (currentPlanPriority >= planPriority(Plans.Basic)) {
    features = {
      ...features,
      [ClientSideFeatures.Term]: 'monthly or annual',
      [ClientSideFeatures.PaymentType]: 'credit card',
      [ClientSideFeatures.ChatSupport]: true,
      [ClientSideFeatures.SessionsLimit]: `up to ${
        sessionsCountOptions.basic[sessionsCountOptions.basic.length - 1].value
      }`,
    };
  }

  if (currentPlanPriority >= planPriority(Plans.Pro)) {
    features = {
      ...features,
      [ClientSideFeatures.DataRetention]: 'up to 3 months',
      [ClientSideFeatures.SessionsLimit]: `up to ${
        sessionsCountOptions.pro[sessionsCountOptions.pro.length - 1].value
      }`,
    };
  }

  if (currentPlanPriority >= planPriority(Plans.Enterprise)) {
    features = {
      ...features,
      [ClientSideFeatures.Term]: 'annual',
      [ClientSideFeatures.PaymentType]: 'credit card or invoice',
      [ClientSideFeatures.CustomPaperwork]: true,
      [ClientSideFeatures.Training]: true,
      [ClientSideFeatures.SessionsLimit]: 'custom',
      [ClientSideFeatures.DataRetention]: 'up to 12 months',
    };
  }

  return features;
}

const pricingFeaturesByPlan = showPlans.reduce(
  (acc, plan) => ({
    ...acc,
    [plan]: pricingModel.features[plan] || {},
  }),
  {},
);

function featureConditions(features) {
  return Object.values(features).reduce((acc, feature) => {
    function getValue(plan) {
      const pricingFeatures = pricingFeaturesByPlan[plan] || {};

      if (Object.prototype.hasOwnProperty.call(pricingFeatures, feature)) {
        const value = (pricingFeatures || {})[feature];
        if (value === ALL_SYMBOL) {
          return true;
        }

        if (feature === Features.Other.PremiumIntegrations) {
          return value && value?.length > 0;
        }

        return value;
      }

      return (clientSideFeatureConditions(plan) || {})[feature];
    }

    return {
      ...acc,
      [feature]: showPlans.reduce(
        (plans, plan) => ({
          ...plans,
          [plan]: getValue(plan),
        }),
        {},
      ),
    };
  }, {});
}

export const FeatureConditions = {
  BillingAndService: featureConditions(Features.BillingAndService),
  Privacy: featureConditions(Features.Privacy),
  Usage: featureConditions(Features.Usage),
  SessionReplays: featureConditions(Features.SessionReplays),
  Analytics: featureConditions(Features.Analytics),
  Other: featureConditions(Features.Other),
};

export const dataSet = [
  {
    name: 'Sessions monthly',
    tooltip: 'Number of sessions which can be recorded every month.',
  },
  {
    name: 'Data retention',
    tooltip: 'Choose for how long your data can be stored.',
  },
  {
    name: 'Websites',
    tooltip: 'Number of websites that can be tracked with one account.',
  },
  { name: 'Users', tooltip: 'Number of people that can use one account.' },
  {
    name: 'Session replays',
    tooltip: 'See mouse movements, clicks, scrolling and more.',
  },
  {
    name: 'Filters',
    tooltip: 'Use a wide range of filtering options to browse your recordings.',
  },
  {
    name: 'Segments',
    tooltip: 'Create custom segments to track different behavioral patterns.',
  },
  {
    name: 'Engagement score',
    tooltip: 'Find and watch the most interesting recordings.',
  },
  {
    name: 'Rage clicks & Error clicks',
    tooltip: 'Spot errors and points of frustration.',
  },
  {
    name: 'Inspect mode',
    tooltip: 'See how users interact with specific elements on your website.',
  },
  {
    name: 'Insights',
    tooltip: 'See top referrers, countries, UTMs, devices and more details.',
  },
  {
    name: 'Single-page application',
    tooltip: 'Works with React, Vue, Angular and other popular frameworks.',
  },
  {
    name: 'Resources sync',
    tooltip: 'CSS, fonts, images are saved to speed up session replays',
  },
  { name: 'Skip inactivity', tooltip: 'Skip idle moments in the recordings.' },
  {
    name: 'Speed up session replays',
    tooltip: 'Speed up recordings to watch them faster.',
  },
  {
    name: 'Tags',
    tooltip: 'Tag session recordings to find them easily later.',
  },
  {
    name: 'Custom properties',
    tooltip:
      'Extend sessions with custom data (e.g. subscription plan) and use it to filter your recordings.',
  },
  {
    name: 'Recording rules',
    tooltip: 'Choose to record sessions based on their URL, IP and other variables.',
  },
  {
    name: 'Always-on recordings',
    tooltip: 'Record all sessions, all the time.',
  },
  {
    name: 'User identification',
    tooltip: 'Identify your users by name or email.',
  },
  { name: 'Segment top lists', tooltip: 'See top results for each segment.' },

  {
    name: 'Premium support',
    tooltip: 'Quick responses and personalized approach via live chat and email.',
  },
  { name: 'Data integrations', tooltip: 'Segment, Google Analytics' },
  {
    name: 'Live chat integrations',
    tooltip: 'Intercom, LiveChat, Drift, Olark, Zendesk Chat, Crisp, HelpScout',
  },
  {
    name: 'Bug tracking integrations',
    tooltip: 'Sentry, Bugsnag, Rollbar, Airbrake, Errorception',
  },
  {
    name: 'Platforms installation',
    tooltip: 'Google Tag Manager, Wordpress, Wix, Shopify, BigCommerce.',
  },
];
